// A cup set (GET /admin/v1/cups/sets/{setId}): its 10 puzzles in play order, rendered with @lumen/puzzle-render
// with the answer highlighted (Hide answers, R), each with its type, category, difficulty, answer and explanation;
// and who was dealt it: the player, the cup and bracket, how far they got, their score, rank and status. Players
// open their page, cups their detail. Specs and answers arrive as stored, so one that doesn't parse shows its JSON.

import { type ReactNode, useState } from "react";
import { Link, useParams } from "react-router";
import { PuzzleView } from "@lumen/puzzle-render";
import { type CupSet, type CupSetHolder, useCupSet } from "../../api/cups";
import { AnswerSchema, PuzzleSpecSchema } from "../../api/schemas";
import { tableStyles } from "../../components/table";
import { Button, Card, CategoryTag, Chip, EmptyState, ErrorState, Loading, PageHeader, Stat } from "../../components/ui";
import { flag, poolName } from "../../lib/cups";
import { formatDateTime, isoWeekSpan } from "../../lib/dates";
import { fmtInt, shortId, typeSlug } from "../../lib/format";
import { useHotkey } from "../../lib/hotkeys";
import l from "../learn/learn.module.css";
import pages from "../pages.module.css";
import { AnswerText } from "../studio/components";
import s from "../studio/studio.module.css";
import { Spread, TypeChips } from "./components";
import c from "./cups.module.css";

const who = (h: { handle: string | null; userId: string }) => (h.handle ? `@${h.handle}` : shortId(h.userId));

export function CupSetPage() {
  const { setId = "" } = useParams();
  const q = useCupSet(setId);
  const [reveal, setReveal] = useState(true);
  useHotkey("r", () => setReveal((v) => !v), Boolean(q.data));
  const back = q.data ? `/tournaments/puzzles?week=${q.data.week}${q.data.pool ? `&pool=${encodeURIComponent(q.data.pool)}` : ""}` : "/tournaments/puzzles";
  const header = (meta?: ReactNode) => (
    <PageHeader
      title={<span className={pages.monoTitle}>{setId}</span>}
      meta={
        <span className={l.statusLine}>
          <Link to={back} className={l.back}>
            ← Cup puzzles
          </Link>
          {meta}
        </span>
      }
      actions={
        q.data ? (
          <Button variant={reveal ? "primary" : "secondary"} aria-pressed={reveal} onClick={() => setReveal((v) => !v)} shortcut="R" data-testid="reveal">
            {reveal ? "Hide answers" : "Show answers"}
          </Button>
        ) : null
      }
    />
  );

  if (q.isPending) {
    return (
      <>
        {header()}
        <Loading rows={10} />
      </>
    );
  }
  if (q.isError) {
    return (
      <>
        {header()}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} title={(q.error as { status?: number }).status === 404 ? "No such cup set" : "Couldn't load this set"} />
      </>
    );
  }

  const set = q.data;
  const categories = new Set(set.puzzles.map((p) => p.category));
  const cups = new Set(set.holders.map((h) => h.cupId));
  return (
    <>
      {header(
        <>
          <span className="mono">
            {set.week} · {isoWeekSpan(set.week)}
          </span>
          {set.pool ? <Chip tone="info">{poolName(set.pool)}</Chip> : <Chip tone="muted">no pool</Chip>}
          <span className="mono">set {set.k}</span>
        </>,
      )}

      <div className={pages.statStrip} data-testid="set-stats">
        <Stat label="Difficulty" value={<Spread mean={set.mean} spread={set.spread} />} sub="mean ± spread" />
        <Stat label="Puzzles" value={fmtInt(set.puzzles.length)} sub={`${categories.size} categories · ${new Set(set.puzzles.map((p) => p.type)).size} types`} />
        <Stat label="Dealt to" value={fmtInt(set.holders.length)} sub={set.holders.length ? `${cups.size === 1 ? "1 cup" : `${cups.size} cups`} this week` : "nobody yet"} />
        <Stat label="Finished" value={fmtInt(set.holders.filter((h) => h.played >= set.puzzles.length).length)} sub={`of ${fmtInt(set.holders.length)} holders`} />
      </div>

      <Holders set={set} />

      <div className={s.toolbar}>
        <TypeChips types={set.puzzles} />
      </div>
      {set.puzzles.length === 0 ? (
        <EmptyState title="This set has no puzzles" />
      ) : (
        <div className={s.itemGrid} data-testid="set-puzzles">
          {set.puzzles.map((p) => (
            <SetPuzzleCard key={p.index} puzzle={p} reveal={reveal} />
          ))}
        </div>
      )}
    </>
  );
}

function Holders({ set }: { set: CupSet }) {
  return (
    <Card title={`Dealt to · ${fmtInt(set.holders.length)}`} padded={false} style={{ marginBottom: 16 }}>
      {set.holders.length === 0 ? (
        <EmptyState title="Not dealt yet">No cup entrant has been dealt this set.</EmptyState>
      ) : (
        <div className={tableStyles.wrap} style={{ border: 0, borderRadius: 0 }}>
          <table className={tableStyles.table}>
            <thead>
              <tr>
                <th>Player</th>
                <th>Cup</th>
                <th data-align="right">Played</th>
                <th data-align="right">Score</th>
                <th data-align="right">Rank</th>
                <th>Dealt</th>
              </tr>
            </thead>
            <tbody>
              {set.holders.map((h) => (
                <HolderRow key={`${h.cupId}:${h.userId}`} h={h} size={set.puzzles.length} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function HolderRow({ h, size }: { h: CupSetHolder; size: number }) {
  return (
    <tr data-testid="holder-row" data-status={h.status ?? undefined} className={h.status === "disqualified" ? c.dqRow : undefined}>
      <td>
        <Link to={`/players/${h.userId}`} className={c.player}>
          {who(h)}
        </Link>{" "}
        <span title={h.country ?? "No known country"}>{h.country ? flag(h.country) : "🌐"}</span>
        {h.status === "disqualified" ? (
          <>
            {" "}
            <Chip tone="danger">disqualified</Chip>
          </>
        ) : null}
      </td>
      <td>
        {h.cupTitle !== null ? (
          <Link to={`/tournaments/${h.cupId}`} className={c.player} data-testid="holder-cup">
            {h.cupTitle || shortId(h.cupId)}
          </Link>
        ) : (
          <span className={c.dim} title={h.cupId}>
            cup {shortId(h.cupId)} is gone
          </span>
        )}
        <span className={c.when}>{h.bracket ?? "no entry"}</span>
      </td>
      <td data-align="right" className={tableStyles.mono}>
        {h.played}/{size}
      </td>
      <td data-align="right" className={tableStyles.mono}>
        {h.score === null ? <span className={c.dim}>—</span> : fmtInt(h.score)}
      </td>
      <td data-align="right" className={tableStyles.mono}>
        {h.rank === null ? <span className={c.dim}>—</span> : `#${h.rank}`}
      </td>
      <td className={c.dim}>{formatDateTime(h.assignedAt)}</td>
    </tr>
  );
}

function SetPuzzleCard({ puzzle: p, reveal }: { puzzle: CupSet["puzzles"][number]; reveal: boolean }) {
  const spec = PuzzleSpecSchema.safeParse(p.spec);
  const answer = AnswerSchema.safeParse(p.answer);
  return (
    <article className={c.puzzleCard} data-testid="set-puzzle" data-type={p.type} aria-label={`Puzzle ${p.index + 1}`}>
      <header className={c.puzzleHead}>
        <span className={c.puzzleN}>{p.index + 1}</span>
        <span className={c.puzzleType} title={p.type}>
          {typeSlug(p.type)}
        </span>
        <CategoryTag category={p.category} />
        <span className={c.puzzleScore} title="difficulty.score">
          {p.difficulty.toFixed(2)}
        </span>
      </header>
      <div className={s.itemStage}>
        {spec.success ? (
          <PuzzleView spec={spec.data} answer={answer.success ? answer.data : null} reveal={reveal} compact />
        ) : (
          <div className={c.badSpec} data-testid="bad-spec">
            <span>This spec doesn&rsquo;t parse as PuzzleSpec v1.</span>
            <pre>{JSON.stringify(p.spec, null, 2)}</pre>
          </div>
        )}
      </div>
      <div className={s.itemText}>
        <span className={pages.answerLine} style={{ marginTop: 0 }}>
          <span className="monoLabel">Answer</span>
          {!reveal ? (
            <span className={pages.hidden}>hidden · press R</span>
          ) : answer.success ? (
            <AnswerText answer={answer.data} />
          ) : (
            <code className={c.mono}>{JSON.stringify(p.answer)}</code>
          )}
        </span>
        {reveal ? (
          <p className={pages.prose} data-testid="explanation">
            {p.explanation || <span className={pages.faint}>No explanation.</span>}
          </p>
        ) : (
          <span className={pages.hidden}>explanation hidden with the answer</span>
        )}
      </div>
      <span className={c.puzzleId}>{p.specId}</span>
    </article>
  );
}
